import { useState } from "react";
import { getData } from "@/lib/utils";
import api from "@/store/api";

const useItems = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch Items
  const fetchItems = async (
    companyId: string,
    pageSize: number = 10,
    pageNumber: number = 1,
    withPaging: boolean = false
  ) => {
    try {
      setIsLoading(true);
      setError(null);
      const Token = getData("tokens")?.accessToken;
      if (!Token) {
        throw new Error("Authentication token is missing");
      }

      const response = await api.get(
        `/web/items/getAll?pageSize=${pageSize}&pageNumber=${pageNumber}&withPaging=${withPaging}&companyId=${companyId}`,
        {
          headers: {
            Authorization: `Bearer ${Token}`,
          },
        }
      );

      return response.data;
    } catch (err: any) {
      console.error(err);
      setError("Failed to fetch items. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };
  const fetchItemSearch = async (
    text: string,
    companyId: string,
    pageSize: number = 10,
    pageNumber: number = 1,
    withPaging: boolean = false
  ) => {
    try {
      setIsLoading(true);
      setError(null);
      const Token = getData("tokens")?.accessToken;
      if (!Token) {
        throw new Error("Authentication token is missing");
      }

      const response = await api.get(
        `/web/items/getAll?pageSize=${pageSize}&pageNumber=${pageNumber}&withPaging=${withPaging}&search=${text}&companyId=${companyId}`,
        {
          headers: {
            Authorization: `Bearer ${Token}`,
          },
        }
      );

      return response.data;
    } catch (err: any) {
      console.error(err);
      setError("Failed to fetch items. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };
  // &search=in&type=Machine&companyId=
  const saveItem = async (newItem: any) => {
    try {
      setIsLoading(true);
      setError(null);
      const Token = getData("tokens")?.accessToken;
      if (!Token) {
        throw new Error("Authentication token is missing");
      }

      const formData = new FormData();
      // Append basic fields
      formData.append("ItemNR", newItem.ItemNR || "0");
      formData.append("name", newItem.name || "");
      formData.append("SKUCode", newItem.SKUCode || "");
      formData.append("manufacturer", newItem.manufacturer || "");
      formData.append("brand", newItem.brand || "");
      formData.append("model", newItem.model || "");
      formData.append("description", newItem.description || "");
      formData.append("price", newItem.price?.toString() || "0");
      formData.append("stock", newItem.stock?.toString() || "0");
      formData.append("type", newItem.type || "");
      formData.append("unit", newItem.unit || "");
      formData.append("tags", JSON.stringify(newItem.tags || []));
      formData.append("companyId", newItem.companyId || "");
      formData.append("userId", newItem.userId || "");
      formData.append("categories", newItem.categories);
      if (newItem.id) formData.append("id", newItem.id);
      if (newItem.vat) formData.append("vat", newItem.vat.toString());

      // Append image
      if (newItem.image instanceof File) {
        formData.append("image", newItem.image, newItem.image.name);
      } else {
        formData.append("imageUrl", newItem.image || "");
      }

      // Append attachments as flat files and send metadata separately

      const response = await api.post(`/web/items/upsert`, formData, {
        headers: {
          Authorization: `Bearer ${Token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      return response.data;
    } catch (err: any) {
      console.error(err);
      setError(
        err.response?.data?.message ||
          "Failed to save the item. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };
  const deleteItem = async (id: string) => {
    try {
      setIsLoading(true);
      setError(null);
      const Token = getData("tokens")?.accessToken;
      if (!Token) {
        throw new Error("Authentication token is missing");
      }
      const response = await api.delete(`/web/items/delete?id=${id}`, {
        headers: {
          Authorization: `Bearer ${Token}`,
        },
      });
      return response.data;
    } catch (err: any) {
      setError("Failed to delete item.");
    } finally {
      setIsLoading(false);
    }
  };
  const fetchItem = async (id: string) => {
    try {
      setIsLoading(true);
      const Token = getData("tokens")?.accessToken;
      if (!Token) {
        throw new Error("Authentication token is missing");
      }

      const response = await api.get(`/web/items/get?id=${id}`, {
        headers: { Authorization: `Bearer ${Token}` },
      });

      return response.data;
    } catch (err: any) {
      setError("Failed to fetch the item. Please try again.");
      console.error(err);
      return null;
    } finally {
      setIsLoading(false);
    }
  };
  return {
    fetchItems,
    fetchItemSearch,
    saveItem,
    deleteItem,
    fetchItem,
    isLoading,
    error,
  };
};
export default useItems;
